import React from "react";
import {
    IonItem,
    IonIcon,
    IonLabel,
    IonBadge,
    IonText,
    IonButton,
    IonSpinner,
} from "@ionic/react";
import { checkmark, close, fitness, sync } from "ionicons/icons";
import { FitnessDataType, FitnessData } from "refsix-js-models";

interface MatchFitnessData {
    matchId: string;
    gpsData?: FitnessData;
    heartRateData?: FitnessData;
    gpsSynced: boolean;
    heartRateSynced: boolean;
}

interface SyncingState {
    [matchId: string]: {
        gps: boolean;
        heartRate: boolean;
    };
}

interface FitnessDataItemProps {
    fitnessData: MatchFitnessData;
    syncing: SyncingState;
    onSync: (matchId: string, dataType: FitnessDataType) => void;
    showDebugInfo?: boolean;
}

export const FitnessDataItem: React.FC<FitnessDataItemProps> = ({
    fitnessData,
    syncing,
    onSync,
    showDebugInfo = false,
}) => {
    const renderSyncStatus = (
        synced: boolean,
        hasData: boolean,
        matchId: string,
        dataType: FitnessDataType
    ) => {
        if (!hasData) {
            return <IonText color="medium">No data</IonText>;
        }

        const isSyncing =
            syncing[matchId]?.[
                dataType === FitnessDataType.GPS ? "gps" : "heartRate"
            ] || false;

        return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <IonBadge color={synced ? "success" : "warning"}>
                    <IonIcon icon={synced ? checkmark : close} />
                    {synced ? "Synced" : "Unsynced"}
                </IonBadge>
                <IonButton
                    size="small"
                    fill="outline"
                    color="primary"
                    disabled={isSyncing}
                    onClick={() => onSync(matchId, dataType)}
                >
                    {isSyncing ? (
                        <IonSpinner
                            name="crescent"
                            style={{ width: "16px", height: "16px" }}
                        />
                    ) : (
                        <IonIcon icon={sync} />
                    )}
                </IonButton>
            </div>
        );
    };

    return (
        <IonItem key={fitnessData.matchId}>
            <IonIcon icon={fitness} slot="start" />
            <IonLabel>
                <h4>{fitnessData.matchId}</h4>
                {showDebugInfo && (
                    <pre>
                        {JSON.stringify(
                            {
                                ...fitnessData,
                                gpsData: null,
                                heartRateData: null,
                            },
                            null,
                            2
                        )}
                    </pre>
                )}
                <div style={{ marginTop: "8px" }}>
                    <div style={{ marginBottom: "8px" }}>
                        <strong>GPS: </strong>
                        {renderSyncStatus(
                            fitnessData.gpsSynced,
                            !!fitnessData.gpsData,
                            fitnessData.matchId,
                            FitnessDataType.GPS
                        )}
                    </div>
                    <div>
                        <strong>Heart Rate: </strong>
                        {renderSyncStatus(
                            fitnessData.heartRateSynced,
                            !!fitnessData.heartRateData,
                            fitnessData.matchId,
                            FitnessDataType.HeartRate
                        )}
                    </div>
                </div>
            </IonLabel>
        </IonItem>
    );
};

export default FitnessDataItem;